import { useCallback, useEffect, useRef } from 'react';
import { createConsumer } from '@rails/actioncable';
import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash/debounce';

interface CommentableParams {
  commentableType: string;
  commentableId: string | number;
}

export const useCommentCable = ({ commentableType, commentableId }: CommentableParams) => {
  const queryClient = useQueryClient();
  const subscriptionRef = useRef<any>(null);

  const onConnected = useCallback(() => {
    console.log('[Comments WebSocket] Connected');
  }, []);

  const onDisconnected = useCallback(() => {
    console.log('[Comments WebSocket] Disconnected');
  }, []);

  const onReceived = useCallback(
    (message: any) => {
      const { type, data } = message;
      console.log(`[Comments WebSocket] Received: ${type}`);

      switch (type) {
        case 'new_comment':
          // Update the comments cache with the new comment
          queryClient.setQueryData(
            ['comments', commentableType, commentableId],
            (oldComments: any[] = []) => {
              return [data.comment, ...oldComments];
            },
          );
          break;

        case 'comment_updated':
          queryClient.setQueryData(
            ['comments', commentableType, commentableId],
            (oldComments: any[] = []) => {
              return oldComments.map((comment) => (comment.id === data.comment.id ? data.comment : comment));
            },
          );
          break;

        case 'comment_deleted':
          queryClient.setQueryData(
            ['comments', commentableType, commentableId],
            (oldComments: any[] = []) => {
              return oldComments.filter((comment) => comment.id !== data.comment_id);
            },
          );
          break;

        case 'typing':
        case 'stopped_typing':
          queryClient.setQueryData(
            ['typing_users', commentableType, commentableId],
            (oldUsers: any[] = []) => {
              if (type === 'typing') {
                const existingUser = oldUsers.find((user) => user.id === data.user_id);
                if (!existingUser) {
                  return [...oldUsers, { id: data.user_id, name: data.user_name }];
                }
              } else {
                return oldUsers.filter((user) => user.id !== data.user_id);
              }
              return oldUsers;
            },
          );
          break;
      }
    },
    [queryClient, commentableType, commentableId],
  );

  const debouncedStoppedTyping = useCallback(
    debounce(() => {
      subscriptionRef.current?.perform('stopped_typing');
    }, 1000),
    [],
  );

  const notifyTyping = useCallback(() => {
    subscriptionRef.current?.perform('typing');
    debouncedStoppedTyping();
  }, [debouncedStoppedTyping]);

  useEffect(() => {
    if (!commentableType || !commentableId) return;

    console.log('[Comments WebSocket] Creating connection...');
    const cable = createConsumer();

    const subscription = cable.subscriptions.create(
      {
        channel: 'CommentsChannel',
        commentable_type: commentableType,
        commentable_id: commentableId,
      },
      {
        connected: onConnected,
        disconnected: onDisconnected,
        received: onReceived,
      },
    );

    subscriptionRef.current = subscription;

    return () => {
      console.log('[Comments WebSocket] Destroying connection...');
      debouncedStoppedTyping.cancel();
      subscription.unsubscribe();
      cable.disconnect();
    };
  }, [commentableType, commentableId, onConnected, onDisconnected, onReceived, debouncedStoppedTyping]);

  return { notifyTyping };
};
